<template>
    <applications-get
        v-slot="{
            data: applications,
            meta,
            error,
            is_loading,
            has_loaded,
            get
        }"
        :params="{
            listing: $route.query.listing,
            page
        }"
        :include="[
            'applicants(paginate:1,5|include:tenant)',
            'listing(include:property)'
        ]"
    >
        <div v-if="!has_loaded || error">
            <base-loading
                    :error="error"
                    :retry="get"
                    class="py-6"
            />

            <error-modal
                    v-if="error"
                    :error="error"
            />
        </div>

        <div v-else>
            <base-hint
                v-if="$gate.role('landlord')"
                name="landlords-applications-index"
            >
                <template slot="heading">
                    Applications
                </template>

                <p>
                    Every Rentora listing comes with a built in application. These applications are sent directly to you, and are listed here.
                </p>
            </base-hint>

            <base-hint
                v-if="$gate.role('tenant')"
                name="tenants-applications-index"
            >
                <template slot="heading">
                    Listing applications
                </template>

                <p class="mb-3">
                    Applications you’ve submitted to property listings are shown here.
                </p>

                <p>
                    Your applications are submitted directly to the landlord/agent. They’ll send you a message once they’ve reviewed your application.
                </p>
            </base-hint>
            <div class="mb-6 bg-white rounded shadow">
                <header class="flex items-center px-4 border-b h-18 sm:px-6">
                    <div class="flex-grow">
                        <h1 class="text-lg font-normal">Applications</h1>
                    </div>
                    <div class="w-6 h-6 mr-3">
                        <base-svg
                            v-show="is_loading"
                            name="icon-loading"
                            class="w-full h-full fill-current text-grey"
                        />
                    </div>
                </header>

                <template v-if="meta.count > 0">
                    <applications-list :applications="applications">
                        <truncate-hack
                            slot="primary"
                            slot-scope="{ application }"
                        >
                            {{ $gate.role('landlord') ? application.listing.property.name : application.listing.property.address.name }}
                        </truncate-hack>

                        <truncate-hack
                            slot="secondary"
                            slot-scope="{ application }"
                        >
                            <template v-if="application.applicants.meta.count > 0">
                                <tenants-current
                                    v-slot="{ data: tenant }"
                                >
                                    {{ application.applicants.data | comma-delimited({
                                        callback: applicant => {
                                            if ($gate.role('tenant') && _.get(applicant, 'tenant.uuid') === tenant.uuid) {
                                                return 'You'
                                            }

                                            return applicant.name
                                        },
                                        total: application.applicants.meta.total
                                    }) }}
                                </tenants-current>
                            </template>
                            <em
                                v-else
                                class="italic text-grey-darker"
                            >
                                No applicants
                            </em>
                        </truncate-hack>
                    </applications-list>

                    <footer class="px-4 text-sm font-semibold rounded-b bg-grey-bg sm:px-6 text-grey-darker">
                        <base-pagination
                            v-model="page"
                            :per-page="meta.per_page"
                            :from="meta.from"
                            :to="meta.to"
                            :last-page="meta.last_page"
                            :count="meta.count"
                            :total="meta.total"
                            :is-disabled="is_loading"
                        />
                    </footer>
                </template>

                <div
                    v-else
                    class="py-16"
                >
                    <div class="max-w-sm px-6 mx-auto text-center">
                        <h2 class="mb-2 text-xl font-semibold">
                            No applications
                        </h2>

                        <div class="text-base text-grey-darker">
                            <template v-if="$gate.role('landlord')">
                                <p class="mb-6">You haven’t received any applications yet.</p>

                                <p>
                                    <router-link
                                        :to="{
                                            name: $router.fix('dashboard.*.listings.create')
                                        }"
                                        class="mx-auto btn btn-with-icon btn-lg"
                                    >
                                        <div class="flex items-center w-full h-full">
                                            <base-svg
                                                    name="icon-add-circle"
                                                    class="w-6 h-6 mr-2"
                                            />

                                            New Listing
                                        </div>
                                    </router-link>
                                </p>
                            </template>

                            <template v-else-if="$gate.role('tenant')">
                                <p class="mb-6">You haven’t submitted any applications yet.</p>

                                <a
                                    :href="$laroute.route('listings.index')"
                                    class="mx-auto btn btn-with-icon btn-lg"
                                    target="_blank"
                                    rel="noopener"
                                >
                                    <div class="flex items-center w-full h-full">
                                        <base-svg
                                            name="icon-external-window"
                                            class="w-6 h-6 mr-2 fill-primary-grey fill-secondary-grey-dark"
                                        />

                                        Find a new home
                                    </div>
                                </a>
                            </template>

                            <template v-else-if="$gate.role('tenant')">
                                <p>There aren't any applications yet.</p>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </applications-get>
</template>

<script>
    import PaginationMixin from '../../../../mixins/pagination'
    import ApplicationsGet from '../../../../components/applications-get'
    import ApplicationsList from '../../../../components/applications-list'
    import TenantsCurrent from '../../../../components/tenants-current'

    export default {
        mixins: [
            PaginationMixin
        ],
        components: {
            ApplicationsGet,
            ApplicationsList,
            TenantsCurrent
        }
    }
</script>
